export async function deriveKey(
    password: string,
    salt: Uint8Array,
): Promise<CryptoKey> {
    const enc = new TextEncoder()
    const keyMaterial = await crypto.subtle.importKey(
        'raw',
        enc.encode(password),
        { name: 'PBKDF2' },
        false,
        ['deriveKey'],
    )
    return crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt: forceArrayBuffer(salt),
            iterations: 100000,
            hash: 'SHA-256',
        },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt'],
    )
}

function forceArrayBuffer(view: Uint8Array): ArrayBuffer {
    // Ensure we pass a real ArrayBuffer (not SharedArrayBuffer) to WebCrypto
    const out = new ArrayBuffer(view.byteLength)
    new Uint8Array(out).set(view)
    return out
}

function toBase64(bytes: Uint8Array): string {
    if (typeof window !== 'undefined' && 'btoa' in window) {
        let binary = ''
        const len = bytes.byteLength
        for (let i = 0; i < len; i++) binary += String.fromCharCode(bytes[i])
        return btoa(binary)
    }
    return Buffer.from(bytes).toString('base64')
}

function fromBase64(b64: string): Uint8Array {
    if (typeof window !== 'undefined' && 'atob' in window) {
        const binary = atob(b64)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
        return bytes
    }
    return new Uint8Array(Buffer.from(b64, 'base64'))
}

export async function encryptString(
    plain: string,
    password: string,
): Promise<string> {
    const iv = crypto.getRandomValues(new Uint8Array(12))
    const salt = crypto.getRandomValues(new Uint8Array(16))
    const key = await deriveKey(password, salt)
    const enc = new TextEncoder()
    const cipher = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        enc.encode(plain),
    )
    const out = new Uint8Array(
        iv.byteLength + salt.byteLength + cipher.byteLength,
    )
    out.set(iv, 0)
    out.set(salt, iv.byteLength)
    out.set(new Uint8Array(cipher), iv.byteLength + salt.byteLength)
    return toBase64(out)
}

export async function decryptString(
    b64: string,
    password: string,
): Promise<string> {
    const data = fromBase64(b64)
    const iv = data.slice(0, 12)
    const salt = data.slice(12, 28)
    const cipher = data.slice(28)
    const key = await deriveKey(password, salt)
    const plainBuf = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        key,
        cipher,
    )
    const dec = new TextDecoder()
    return dec.decode(plainBuf)
}

export function setCredentialsPasswordCookie(password: string): void {
    if (typeof document === 'undefined') return
    // HttpOnly cannot be set from JS; use Secure + SameSite and short max-age.
    const maxAge = 60 * 60 * 24 * 30 // 30 days
    document.cookie = `credentialsPassword=${encodeURIComponent(password)}; Max-Age=${maxAge}; Path=/; Secure; SameSite=Strict`
}

export function getCredentialsPasswordCookie(): string | null {
    if (typeof document === 'undefined') return null
    const name = 'credentialsPassword='
    const parts = document.cookie.split(';')
    for (const part of parts) {
        const p = part.trim()
        if (p.startsWith(name))
            return decodeURIComponent(p.substring(name.length))
    }
    return null
}
